/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, map, shareReplay } from "rxjs";
import { SANITY_CDN_QUERY_URL, cloudUiBannerQuery, SiteBanner, SANITY_DRAFT_QUERY_URL } from "typedb-web-common/lib";
import {
    cloudLoginPortalSchemaName,
    CloudOnboarding,
    cloudOnboardingSchemaName,
    CloudAnnouncement,
    ParagraphWithHighlights,
    PortableText,
    ProviderRegionData,
    providerRegionsQuery,
    SanitySurvey,
    Survey,
    cloudAnnouncementQuery,
} from "typedb-web-schema/lib";
import { environment } from "../environments/environment";

type SanityLoginPortalData = {
    title: PortableText,
    subtitle: PortableText,
    keyPoints: string[],
};

type LoginPortalData = {
    title: ParagraphWithHighlights,
    subtitle: ParagraphWithHighlights,
    keyPoints: string[],
};

const loginPortalQuery = `*[(_id match '${cloudLoginPortalSchemaName}')][0]`;
const onboardingQuery = `*[(_id match '${cloudOnboardingSchemaName}')][0]`;
const projectInfoSurveyQuery = `*[_id == '3f83b3fb-c14c-4ae3-80d0-880eb0ffc14c'][0]`;

@Injectable({
    providedIn: "root",
})
export class SanityService {
    readonly loginPortal: Observable<LoginPortalData>;
    readonly onboarding: Observable<CloudOnboarding>;
    readonly projectInfoSurvey: Observable<Survey>;
    readonly providerRegionData: Observable<ProviderRegionData>;
    readonly appBanner: Observable<SiteBanner>;
    readonly announcement: Observable<CloudAnnouncement>;

    constructor(private http: HttpClient) {
        this.loginPortal = this.getSanityResult<SanityLoginPortalData>(loginPortalQuery).pipe(
            map(data => ({
                title: ParagraphWithHighlights.fromSanity(data.title),
                subtitle: ParagraphWithHighlights.fromSanity(data.subtitle),
                keyPoints: data.keyPoints,
            })),
            shareReplay(1),
        )
        this.appBanner = this.getSanityResult<{ cloudUiBanner: SiteBanner }>(cloudUiBannerQuery).pipe(
            map(x => x.cloudUiBanner),
            shareReplay(1),
        );
        this.projectInfoSurvey = this.getSanityResult<SanitySurvey>(projectInfoSurveyQuery).pipe(
            map(data => Survey.fromSanity(data)),
            shareReplay(1),
        );
        this.providerRegionData = this.getSanityResult<ProviderRegionData>(providerRegionsQuery).pipe(shareReplay(1));
        this.onboarding = this.getSanityResult<CloudOnboarding>(onboardingQuery).pipe(shareReplay(1));
        this.announcement = this.getSanityResult<{ cloudAnnouncement: CloudAnnouncement }>(cloudAnnouncementQuery).pipe(
            map(x => x.cloudAnnouncement),
            shareReplay(1)
        );

        [
            this.loginPortal,
            this.appBanner,
            this.projectInfoSurvey,
            this.providerRegionData,
            this.onboarding,
            this.announcement,
        ].forEach((x: Observable<any>) => x.subscribe());
    }

    private getSanityResult<T>(query: string) {
        return this.http.get<{ result: T }>(
            environment.env === "production" ? SANITY_CDN_QUERY_URL : `https://development.typedb.com${SANITY_DRAFT_QUERY_URL}`,
            environment.env === "production"
                ? { params: { query, perspective: "published" } }
                : { params: { query } },
        ).pipe(map(({ result }) => result));
    }
}
